import { isAxiosError } from 'axios';
import { AlertTriangle, Link2, Video, VideoOff } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type {
  FulfillmentAdminClient,
  PackingVideo,
  PackingVideoList,
  PackingVideoShareLink,
} from '@/infrastructure/FulfillmentAdminClient';

/**
 * Zero Packing packing videos on the order screens (SSD-7372). The list is read live by internal-api; a video's file
 * link is fetched only when someone unfolds it, because Zero Packing's link dies in about ten minutes.
 */
export type PackingVideosState =
  | { phase: 'loading' }
  | { phase: 'loaded'; list: PackingVideoList }
  | { phase: 'failed'; message: string };

/** Where the videos are drawn: FF2 Order Detail, OMS Center order detail, or the Closing dialog. */
export type PackingVideosVariant = 'ff2' | 'oms' | 'modal';

const kst = (iso: string, withTime = true): string => {
  const at = new Date(new Date(iso).getTime() + 9 * 3600 * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  const date = `${at.getUTCFullYear()}-${pad(at.getUTCMonth() + 1)}-${pad(at.getUTCDate())}`;
  return withTime ? `${date} ${pad(at.getUTCHours())}:${pad(at.getUTCMinutes())}` : date;
};

const mmss = (seconds: number | null): string => {
  if (seconds == null) {
    return '—';
  }
  // Round the whole first, so 59.5 s reads 1:00 rather than 0:00.
  const total = Math.round(seconds);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

const failureMessage = (error: unknown, reading: 'list' | 'video' = 'video'): string => {
  if (isAxiosError(error)) {
    const status = error.response?.status;
    if (status === 503) {
      return 'Zero Packing is not connected on this server yet.';
    }
    if (status === 410) {
      return 'Zero Packing has already deleted this video (kept 90 days).';
    }
    if (status === 404) {
      return reading === 'list'
        ? 'This order was not found.'
        : 'This video is no longer listed for this order. Refresh the list.';
    }
    if (status === 502) {
      return 'Zero Packing did not answer. Try again in a moment.';
    }
  }
  return 'Could not load from Zero Packing. Try again in a moment.';
};

/** One read of the order's list, with a reload for the Refresh button. A newer read always wins over a late older one. */
export const usePackingVideos = (client: FulfillmentAdminClient, orderId: string | number | null) => {
  const [state, setState] = useState<PackingVideosState>({ phase: 'loading' });
  const latest = useRef(0);

  const reload = useCallback(async () => {
    if (orderId == null || orderId === '') {
      return;
    }
    const ticket = ++latest.current;
    setState({ phase: 'loading' });
    try {
      const list = await client.listPackingVideos(orderId);
      if (ticket === latest.current) {
        setState({ phase: 'loaded', list });
      }
    } catch (error) {
      if (ticket === latest.current) {
        setState({ phase: 'failed', message: failureMessage(error, 'list') });
      }
    }
  }, [client, orderId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { state, reload };
};

const buttonClass = (variant: PackingVideosVariant): string =>
  variant === 'ff2' ? 'btn btn-line btn-sm' : 'btn btn-outline-primary btn-sm';

/** The player under an unfolded row. A link that expired while the page sat open is fetched again once. */
const PackingVideoPlayer = ({
  client,
  orderId,
  video,
  height,
}: {
  client: FulfillmentAdminClient;
  orderId: string | number;
  video: PackingVideo;
  height: number;
}) => {
  const [url, setUrl] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const retried = useRef(false);

  const fetchLink = useCallback(async () => {
    setMessage(null);
    try {
      const playback = await client.getPackingVideoPlayback(orderId, video.videoId);
      setUrl(playback.url);
    } catch (error) {
      setUrl(null);
      setMessage(failureMessage(error));
    }
  }, [client, orderId, video.videoId]);

  useEffect(() => {
    void fetchLink();
  }, [fetchLink]);

  if (message) {
    return (
      <div className="blocked-note" data-testid="packing-video-error">
        {message}
      </div>
    );
  }
  if (!url) {
    return <div className="blocked-note">Loading the video…</div>;
  }
  return (
    <video
      key={url}
      data-testid="packing-video-player"
      src={url}
      // Unfolding only shows the player; the operator presses play.
      controls
      playsInline
      preload="metadata"
      onError={() => {
        if (!retried.current) {
          retried.current = true;
          void fetchLink();
        } else {
          setMessage('The video did not play. Refresh the list and try again.');
        }
      }}
      style={{ display: 'block', width: '100%', maxWidth: 640, height, background: '#111', borderRadius: 6 }}
    />
  );
};

/** The fallback copy: a hidden textarea and execCommand, for pages that refuse the async clipboard. */
const copyWithTextarea = (text: string): boolean => {
  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.appendChild(area);
  area.select();
  const copied = document.execCommand('copy');
  area.remove();
  return copied;
};

/** How long the async clipboard may take before the fallback is used; it can sit unanswered on an unfocused page. */
const CLIPBOARD_WAIT_MS = 1500;

/** Copies text even where the async clipboard is refused or never answers (an embedded or non-secure page). */
const copyText = async (text: string): Promise<boolean> => {
  try {
    const answered = await Promise.race([
      navigator.clipboard.writeText(text).then(() => true),
      new Promise<false>((resolve) => {
        setTimeout(() => resolve(false), CLIPBOARD_WAIT_MS);
      }),
    ]);
    return answered || copyWithTextarea(text);
  } catch {
    return copyWithTextarea(text);
  }
};

type ShareState =
  | { phase: 'idle' }
  | { phase: 'working' }
  | { phase: 'done'; link: PackingVideoShareLink; copied: boolean; flash: boolean }
  | { phase: 'failed'; message: string };

const COLORS = {
  line: '#e3e6ea',
  muted: '#6c757d',
  ink: '#1f2937',
  blue: '#0d6efd',
  blueSoft: '#e7f1ff',
  amberSoft: '#fff4e5',
  amberLine: '#f5c27a',
  redSoft: '#fdecee',
  redLine: '#f1aeb5',
};

const chipStyle = {
  display: 'inline-block',
  padding: '1px 8px',
  borderRadius: 999,
  background: '#f1f3f5',
  color: COLORS.ink,
  fontSize: 12,
  fontWeight: 600,
} as const;

/** What the share button made, under the row: the link itself and how long it works. */
const ShareLinkNote = ({ share }: { share: ShareState }) => {
  if (share.phase === 'failed') {
    return (
      <div data-testid="packing-video-share-note" style={{ marginTop: 8, fontSize: 12.5, color: '#b02a37' }}>
        {share.message}
      </div>
    );
  }
  if (share.phase !== 'done') {
    return null;
  }
  return (
    <div
      data-testid="packing-video-share-note"
      style={{
        marginTop: 10,
        padding: '8px 10px',
        borderRadius: 8,
        background: COLORS.blueSoft,
        fontSize: 12.5,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        alignItems: 'center',
      }}
    >
      <span style={{ fontWeight: 700, color: COLORS.ink }}>
        {share.copied ? 'Copied for the customer' : 'Share link (copy it below)'}
      </span>
      <a href={share.link.url} target="_blank" rel="noreferrer" style={{ wordBreak: 'break-all', color: COLORS.blue }}>
        {share.link.url}
      </a>
      <span style={{ color: COLORS.muted }}>Opens without sign-in until {kst(share.link.expiresAt, false)}</span>
    </div>
  );
};

/**
 * The customer share page for one video: made on press (Zero Packing reuses the open one for the same video) and
 * copied to the clipboard; its state goes up so the link and its end date can sit under the whole row.
 */
const ShareLinkButton = ({
  client,
  orderId,
  video,
  variant,
  onChange,
}: {
  client: FulfillmentAdminClient;
  orderId: string | number;
  video: PackingVideo;
  variant: PackingVideosVariant;
  onChange: (share: ShareState) => void;
}) => {
  const [share, setShare] = useState<ShareState>({ phase: 'idle' });
  const update = (next: ShareState) => {
    setShare(next);
    onChange(next);
  };

  useEffect(() => {
    if (share.phase !== 'done' || !share.flash) {
      return undefined;
    }
    const timer = setTimeout(() => update({ ...share, flash: false }), 2000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [share]);

  const press = async () => {
    if (share.phase === 'done') {
      const copied = await copyText(share.link.url);
      update({ ...share, copied, flash: copied });
      return;
    }
    update({ phase: 'working' });
    let link: PackingVideoShareLink;
    try {
      link = await client.createPackingVideoShareLink(orderId, video.videoId);
    } catch (error) {
      update({ phase: 'failed', message: failureMessage(error) });
      return;
    }
    // The link shows as soon as it exists; copying is a bonus and never holds it back.
    update({ phase: 'done', link, copied: false, flash: false });
    const copied = await copyText(link.url);
    update({ phase: 'done', link, copied, flash: copied });
  };

  const label =
    share.phase === 'working'
      ? 'Making link…'
      : share.phase === 'done' && share.flash
        ? '✓ Link copied'
        : share.phase === 'done'
          ? 'Copy link again'
          : 'Copy share link';

  return (
    <button
      type="button"
      className={buttonClass(variant)}
      data-testid="packing-video-share"
      disabled={video.expired || share.phase === 'working'}
      // A fixed width so the row does not shift while the label changes.
      style={{
        whiteSpace: 'nowrap',
        width: 150,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
      }}
      onClick={() => void press()}
    >
      <Link2 size={14} aria-hidden />
      {label}
    </button>
  );
};

/** One video as a card: when and where it was packed, Show video to unfold the player, and the customer share link. */
const PackingVideoRow = ({
  client,
  orderId,
  video,
  open,
  onToggle,
  variant,
  latest,
}: {
  client: FulfillmentAdminClient;
  orderId: string | number;
  video: PackingVideo;
  open: boolean;
  onToggle: () => void;
  variant: PackingVideosVariant;
  latest: boolean;
}) => {
  const [share, setShare] = useState<ShareState>({ phase: 'idle' });
  return (
    <div
      data-testid="packing-video-row"
      style={{
        border: `1px solid ${open ? COLORS.blue : COLORS.line}`,
        borderRadius: 10,
        padding: '12px 14px',
        background: '#fff',
        marginTop: 10,
        opacity: video.expired ? 0.65 : 1,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        {/* The two actions sit together on the left, so the share link is one short move from Show video. */}
        <div
          data-testid="packing-video-actions"
          style={{ display: 'flex', alignItems: 'center', gap: 6, flex: '0 0 auto' }}
        >
          <button
            type="button"
            className={buttonClass(variant)}
            aria-expanded={open}
            disabled={video.expired}
            // A fixed width so the row does not shift when the label changes between Show and Hide.
            style={{ whiteSpace: 'nowrap', width: 124 }}
            onClick={onToggle}
          >
            {open ? '▾ Hide video' : '▸ Show video'}
          </button>
          <ShareLinkButton client={client} orderId={orderId} video={video} variant={variant} onChange={setShare} />
        </div>
        <div style={{ flex: '1 1 240px', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, color: COLORS.ink, whiteSpace: 'nowrap' }}>
              {kst(video.recordedAt)} KST
            </span>
            {latest && (
              <span
                style={{ ...chipStyle, background: COLORS.blueSoft, color: COLORS.blue }}
                data-testid="packing-video-latest"
              >
                Latest
              </span>
            )}
            <span style={chipStyle}>{video.stationName ?? 'station —'}</span>
            <span style={chipStyle}>{mmss(video.durationSeconds)}</span>
          </div>
          <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 3 }}>
            {video.expired
              ? `Deleted by Zero Packing on ${video.expiresAt ? kst(video.expiresAt, false) : '—'}`
              : `Kept until ${video.expiresAt ? kst(video.expiresAt, false) : '—'}`}
            {video.scannedNumber &&
              String(video.scannedNumber) !== String(orderId) &&
              ` · scanned as ${video.scannedNumber}`}
          </div>
        </div>
      </div>
      <ShareLinkNote share={share} />
      {open && (
        <div style={{ marginTop: 12 }}>
          <PackingVideoPlayer
            client={client}
            orderId={orderId}
            video={video}
            height={variant === 'modal' ? 260 : 360}
          />
        </div>
      )}
    </div>
  );
};

/** A framed message in place of the list: empty, not connected, or failed (with a retry). */
const StateBox = ({
  tone,
  title,
  detail,
  action,
}: {
  tone: 'empty' | 'warn' | 'error';
  title: string;
  detail?: string;
  action?: ReactNode;
}) => {
  const look = {
    empty: { border: `1px dashed ${COLORS.line}`, background: '#f8fafc', icon: <Video size={20} aria-hidden /> },
    warn: {
      border: `1px solid ${COLORS.amberLine}`,
      background: COLORS.amberSoft,
      icon: <VideoOff size={20} aria-hidden />,
    },
    error: {
      border: `1px solid ${COLORS.redLine}`,
      background: COLORS.redSoft,
      icon: <AlertTriangle size={20} aria-hidden />,
    },
  }[tone];
  return (
    <div
      data-testid="packing-videos-state"
      style={{
        border: look.border,
        background: look.background,
        borderRadius: 10,
        padding: '14px 16px',
        marginTop: 10,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <span
        style={{
          width: 36,
          height: 36,
          borderRadius: 999,
          background: '#fff',
          border: `1px solid ${COLORS.line}`,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: COLORS.muted,
          flex: '0 0 auto',
        }}
      >
        {look.icon}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: COLORS.ink }}>{title}</div>
        {detail && <div style={{ fontSize: 12.5, color: COLORS.muted, marginTop: 2 }}>{detail}</div>}
      </div>
      {action}
    </div>
  );
};

/** What the list area says while it is not a list of videos. */
const PackingVideosStatus = ({
  state,
  onReload,
  variant,
}: {
  state: PackingVideosState;
  onReload: () => void;
  variant: PackingVideosVariant;
}) => {
  if (state.phase === 'loading') {
    return (
      <div className="blocked-note" style={{ marginTop: 10 }}>
        Looking up packing videos…
      </div>
    );
  }
  if (state.phase === 'failed') {
    return (
      <StateBox
        tone="error"
        title={state.message}
        action={
          <button type="button" className={buttonClass(variant)} onClick={onReload}>
            ↻ Try again
          </button>
        }
      />
    );
  }
  if (!state.list.connected) {
    return <StateBox tone="warn" title="Zero Packing is not connected on this server yet." />;
  }
  if (state.list.videos.length === 0) {
    return (
      <StateBox
        tone="empty"
        title="No packing video yet."
        detail="A video shows up here a few seconds after packing ends."
      />
    );
  }
  return null;
};

/** The list itself, shared by all three places; every row starts folded (the Closing dialog shows only the latest). */
const PackingVideoList = ({
  client,
  orderId,
  state,
  onReload,
  variant,
  limit,
}: {
  client: FulfillmentAdminClient;
  orderId: string | number;
  state: PackingVideosState;
  onReload: () => void;
  variant: PackingVideosVariant;
  limit?: number;
}) => {
  const [openId, setOpenId] = useState<string | null>(null);
  const status = <PackingVideosStatus state={state} onReload={onReload} variant={variant} />;
  if (state.phase !== 'loaded' || !state.list.connected || state.list.videos.length === 0) {
    return status;
  }
  const videos = limit ? state.list.videos.slice(0, limit) : state.list.videos;
  const several = state.list.videos.length > 1;
  return (
    <div data-testid="packing-video-list">
      {several && !limit && (
        <div style={{ fontSize: 12.5, color: COLORS.muted, marginTop: 6 }}>
          More than one video usually means the box was repacked. The newest is on top.
        </div>
      )}
      {videos.map((video, index) => (
        <PackingVideoRow
          key={video.videoId}
          client={client}
          orderId={orderId}
          video={video}
          variant={variant}
          latest={several && index === 0}
          open={openId === video.videoId}
          onToggle={() => setOpenId((current) => (current === video.videoId ? null : video.videoId))}
        />
      ))}
    </div>
  );
};

const videoCount = (state: PackingVideosState): number =>
  state.phase === 'loaded' && state.list.connected ? state.list.videos.length : 0;

/** Title-row marker on FF2 Order Detail: says only that videos exist, and jumps to the section. */
export const PackingVideosChip = ({ state }: { state: PackingVideosState }) => {
  const count = videoCount(state);
  if (count === 0) {
    return null;
  }
  return (
    <a
      href="#packing-videos"
      data-testid="packing-videos-chip"
      onClick={(event) => {
        event.preventDefault();
        document.getElementById('packing-videos')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }}
      style={{
        background: 'var(--blue-soft, #eef3fc)',
        color: 'var(--blue, #2457c5)',
        border: '1px solid var(--blue, #2457c5)',
        borderRadius: 6,
        padding: '2px 10px',
        fontSize: 11.5,
        fontWeight: 800,
        textDecoration: 'none',
      }}
    >
      ▶ Packing videos · {count}
    </a>
  );
};

const NOTE = 'From Zero Packing · newest first · kept 90 days';

/** The section on FF2 Order Detail (above Fulfillment Tracking) or OMS Center order detail (a card above the carrier cards). */
export const PackingVideosSection = ({
  client,
  orderId,
  state,
  onReload,
  variant,
}: {
  client: FulfillmentAdminClient;
  orderId: string | number;
  state: PackingVideosState;
  onReload: () => void;
  variant: 'ff2' | 'oms';
}) => {
  const count = videoCount(state);
  const refresh = (
    <button type="button" className={buttonClass(variant)} disabled={state.phase === 'loading'} onClick={onReload}>
      ↻ Refresh
    </button>
  );
  const list = (
    <PackingVideoList client={client} orderId={orderId} state={state} onReload={onReload} variant={variant} />
  );

  if (variant === 'oms') {
    return (
      <Card id="packing-videos" data-testid="packing-videos">
        <CardHeader className="py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Video className="h-5 w-5 text-muted-foreground" />
              <CardTitle>Packing Videos{count > 0 ? ` (${count})` : ''}</CardTitle>
            </div>
            {refresh}
          </div>
          <div className="text-muted small">{NOTE}</div>
        </CardHeader>
        <CardContent>{list}</CardContent>
      </Card>
    );
  }
  return (
    <div className="sec" id="packing-videos" data-testid="packing-videos">
      <h3>🎥 Packing Videos{count > 0 ? ` (${count})` : ''}</h3>
      <div className="ftbadge">
        <span className="blocked-note" style={{ margin: 0 }}>
          {NOTE}
        </span>
        {refresh}
      </div>
      {list}
    </div>
  );
};

/** Inside the Closing "Process Processing Order" dialog, right above the Zero Packing checkbox: the latest video, folded. */
export const PackingVideoInline = ({
  client,
  orderId,
}: {
  client: FulfillmentAdminClient;
  orderId: string | number;
}) => {
  const { state, reload } = usePackingVideos(client, orderId);
  const count = videoCount(state);
  return (
    <div className="mt-3" data-testid="packing-video-inline">
      <div className="text-muted small text-uppercase mb-1" style={{ letterSpacing: 0.4 }}>
        Packing video{count > 1 ? ` · latest of ${count}` : ''}
      </div>
      <PackingVideoList
        client={client}
        orderId={orderId}
        state={state}
        onReload={() => void reload()}
        variant="modal"
        limit={1}
      />
    </div>
  );
};
